// Client-side auth token/session storage.
// Business-critical timestamps + audit stay server-side (CLAUDE.md); this only
// persists the bearer token so the SPA can authenticate API calls.
//
// "Remember me" -> localStorage (survives browser restart).
// Otherwise    -> sessionStorage (cleared when the tab closes / session timeout).

import type { AuthUser } from "./api";

const TOKEN_KEY = "bell.token";
const USER_KEY = "bell.user";

function stores(): Storage[] {
  if (typeof window === "undefined") return [];
  return [window.localStorage, window.sessionStorage];
}

export function getToken(): string | null {
  for (const s of stores()) {
    const t = s.getItem(TOKEN_KEY);
    if (t) return t;
  }
  return null;
}

export function setToken(token: string, remember: boolean): void {
  if (typeof window === "undefined") return;
  const primary = remember ? window.localStorage : window.sessionStorage;
  const other = remember ? window.sessionStorage : window.localStorage;
  other.removeItem(TOKEN_KEY);
  primary.setItem(TOKEN_KEY, token);
}

export function getStoredUser(): AuthUser | null {
  for (const s of stores()) {
    const raw = s.getItem(USER_KEY);
    if (raw) {
      try {
        return JSON.parse(raw) as AuthUser;
      } catch {
        return null;
      }
    }
  }
  return null;
}

export function setStoredUser(user: AuthUser, remember: boolean): void {
  if (typeof window === "undefined") return;
  const primary = remember ? window.localStorage : window.sessionStorage;
  const other = remember ? window.sessionStorage : window.localStorage;
  other.removeItem(USER_KEY);
  primary.setItem(USER_KEY, JSON.stringify(user));
}

export function clearSession(): void {
  for (const s of stores()) {
    s.removeItem(TOKEN_KEY);
    s.removeItem(USER_KEY);
  }
}

export function isAuthenticated(): boolean {
  return getToken() !== null;
}
